import React, { useRef, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowLeft, ExternalLink, Github, Calendar, Users, Code, Zap, Target, AlertTriangle, FileText, Image, Download } from 'lucide-react';
import { useTheme } from '../ThemeContext';
import { projectsData } from '../data/projects';
import Navbar from './Navbar';
import ScanlineOverlay from './ScanlineOverlay';
import Footer from './Footer';

function ScrollToTop() {
  const { projectId } = useParams();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [projectId]);
  return null;
}

function StatCard({ icon: Icon, label, value, isCyberpunk, delay }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay }}
      className={`p-4 text-center transition-all duration-500 ${
        isCyberpunk
          ? 'neon-border bg-cyber-dark/50'
          : 'bg-white border border-gray-200 rounded-xl shadow-sm'
      }`}
    >
      <Icon size={20} className={`mx-auto mb-2 ${isCyberpunk ? 'text-cyber-magenta' : 'text-indigo-500'}`} />
      <p className={`font-mono text-xs uppercase tracking-wider mb-1 ${
        isCyberpunk ? 'text-cyber-dim/50' : 'text-gray-400'
      }`}>{label}</p>
      <p className={`font-display text-lg font-bold ${
        isCyberpunk ? 'text-white' : 'text-gray-900'
      }`}>{value}</p>
    </motion.div>
  );
}

function DetailSection({ title, cyberTitle, children, isCyberpunk, delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
      className="mb-10"
    >
      <h3 className={`font-display text-xl font-semibold mb-4 tracking-wide transition-colors duration-500 ${
        isCyberpunk ? 'text-cyber-magenta' : 'text-gray-800'
      }`}>
        {isCyberpunk ? cyberTitle : title}
      </h3>
      {children}
    </motion.div>
  );
}

export default function ProjectDetail() {
  const { projectId } = useParams();
  const { isCyberpunk } = useTheme();
  const project = projectsData.find((p) => p.id === projectId);

  if (!project) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isCyberpunk ? 'bg-cyber-black text-white' : 'bg-white text-gray-900'
      }`}>
        <div className="text-center">
          <h1 className="font-display text-4xl font-bold mb-4">404</h1>
          <p className={`font-body mb-6 ${isCyberpunk ? 'text-cyber-dim' : 'text-gray-500'}`}>
            {isCyberpunk ? 'ERROR: Project not found in database' : 'Project not found'}
          </p>
          <Link
            to="/"
            className={`font-mono text-sm px-6 py-3 border inline-flex items-center gap-2 ${
              isCyberpunk
                ? 'border-cyber-magenta text-cyber-magenta hover:bg-cyber-magenta/10'
                : 'border-indigo-600 text-indigo-600 hover:bg-indigo-50 rounded-lg'
            }`}
          >
            <ArrowLeft size={16} /> Go back
          </Link>
        </div>
      </div>
    );
  }

  const accent = isCyberpunk ? project.borderColor : project.cleanBorderColor;

  return (
    <div className={`min-h-screen transition-colors duration-700 ${
      isCyberpunk ? 'bg-cyber-black' : 'bg-white'
    }`}>
      <ScrollToTop />
      <ScanlineOverlay />
      <Navbar />

      <main className={`pt-28 pb-20 px-6 ${isCyberpunk ? 'cyber-grid' : ''}`}>
        <div className="max-w-5xl mx-auto">
          {/* Back link */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Link
              to="/#projects"
              className={`inline-flex items-center gap-2 font-mono text-sm mb-8 transition-colors ${
                isCyberpunk
                  ? 'text-cyber-dim hover:text-cyber-magenta'
                  : 'text-gray-400 hover:text-indigo-600'
              }`}
            >
              <ArrowLeft size={16} />
              {isCyberpunk ? 'BACK_TO_PROJECTS' : 'Back to projects'}
            </Link>
          </motion.div>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className={`p-8 mb-8 transition-all duration-500 ${
              isCyberpunk
                ? 'neon-border corner-accents bg-cyber-dark/60'
                : 'bg-white border border-gray-200 rounded-2xl shadow-lg'
            }`}
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div>
                <p className="font-mono text-xs tracking-wider mb-2" style={{ color: accent }}>
                  {project.category}
                </p>
                <h1 className={`font-display text-3xl sm:text-4xl font-bold tracking-wider transition-colors duration-500 ${
                  isCyberpunk ? 'text-white' : 'text-gray-900'
                }`}>
                  {project.title}
                </h1>
              </div>
              <span className={`self-start font-mono text-xs px-3 py-1.5 tracking-wider ${
                isCyberpunk
                  ? 'bg-cyber-magenta/10 border border-cyber-magenta/30 text-cyber-magenta'
                  : 'bg-indigo-50 text-indigo-600 rounded-full'
              }`}>
                {project.status}
              </span>
            </div>

            <p className={`font-body text-lg leading-relaxed mb-6 transition-colors duration-500 ${
              isCyberpunk ? 'text-cyber-dim/80' : 'text-gray-600'
            }`}>
              {project.longDescription}
            </p>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-4">
              {project.github && (
                <a
                  href={project.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-2 font-mono text-sm px-5 py-2.5 border transition-all duration-300 ${
                    isCyberpunk
                      ? 'border-cyber-magenta/50 text-cyber-magenta hover:bg-cyber-magenta/10'
                      : 'border-gray-300 text-gray-700 hover:border-indigo-400 hover:text-indigo-600 rounded-lg'
                  }`}
                >
                  <Github size={16} />
                  {isCyberpunk ? 'VIEW_SOURCE' : 'View Source'}
                </a>
              )}
              {project.demo && (
                <a
                  href={project.demo}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-2 font-mono text-sm px-5 py-2.5 border transition-all duration-300 ${
                    isCyberpunk
                      ? 'border-cyber-purple/50 text-cyber-purple hover:bg-cyber-purple/10'
                      : 'border-indigo-400 text-indigo-600 hover:bg-indigo-50 rounded-lg'
                  }`}
                >
                  <ExternalLink size={16} />
                  {isCyberpunk ? 'LIVE_DEMO' : 'Live Demo'}
                </a>
              )}
            </div>
          </motion.div>

          {/* Stats row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Calendar} label="Timeline" value={project.timeline} isCyberpunk={isCyberpunk} delay={0.2} />
            <StatCard icon={Users} label="Team" value={project.team} isCyberpunk={isCyberpunk} delay={0.3} />
            <StatCard icon={Code} label="Stack Size" value={`${project.techStack.length} tools`} isCyberpunk={isCyberpunk} delay={0.4} />
            <StatCard icon={Zap} label="Status" value={project.status} isCyberpunk={isCyberpunk} delay={0.5} />
          </div>

          {/* Two column layout */}
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Left column */}
            <div>
              <DetailSection
                title="Key Features"
                cyberTitle="FEATURES"
                isCyberpunk={isCyberpunk}
                delay={0.1}
              >
                <div className="space-y-3">
                  {project.features.map((feature, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.05 }}
                      className="flex items-start gap-3"
                    >
                      <span className="mt-1" style={{ color: accent }}>
                        {isCyberpunk ? '>' : '•'}
                      </span>
                      <span className={`font-body text-sm leading-relaxed ${
                        isCyberpunk ? 'text-cyber-dim/80' : 'text-gray-600'
                      }`}>{feature}</span>
                    </motion.div>
                  ))}
                </div>
              </DetailSection>

              {project.impact && (
                <DetailSection
                  title="Project Impact"
                  cyberTitle="IMPACT_ANALYSIS"
                  isCyberpunk={isCyberpunk}
                  delay={0.2}
                >
                  <div className={`p-5 transition-all duration-500 ${
                    isCyberpunk
                      ? 'bg-cyber-dark/50 border border-cyber-magenta/10'
                      : 'bg-gray-50 rounded-xl'
                  }`}>
                    <Target size={18} className={`mb-3 ${isCyberpunk ? 'text-cyber-magenta' : 'text-indigo-500'}`} />
                    <p className={`font-body text-sm leading-relaxed ${
                      isCyberpunk ? 'text-cyber-dim/80' : 'text-gray-600'
                    }`}>{project.impact}</p>
                  </div>
                </DetailSection>
              )}
            </div>

            {/* Right column */}
            <div>
              <DetailSection
                title="Technology Stack"
                cyberTitle="TECH_STACK"
                isCyberpunk={isCyberpunk}
                delay={0.15}
              >
                <div className="grid grid-cols-2 gap-2">
                  {project.techStack.map((tech, i) => (
                    <motion.div
                      key={tech}
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.04 }}
                      className={`text-center font-mono text-xs p-2.5 transition-all duration-500 ${
                        isCyberpunk
                          ? 'border border-cyber-magenta/15 bg-cyber-dark/50 text-cyber-dim/80 hover:border-cyber-magenta/40 hover:text-cyber-magenta'
                          : 'border border-gray-200 bg-white text-gray-700 rounded-lg hover:border-indigo-300 hover:text-indigo-600'
                      }`}
                    >
                      {tech}
                    </motion.div>
                  ))}
                </div>
              </DetailSection>

              {project.challenges && (
                <DetailSection
                  title="Technical Challenges"
                  cyberTitle="CHALLENGES"
                  isCyberpunk={isCyberpunk}
                  delay={0.25}
                >
                  <div className={`p-5 transition-all duration-500 ${
                    isCyberpunk
                      ? 'bg-cyber-dark/50 border border-cyber-magenta/10'
                      : 'bg-gray-50 rounded-xl'
                  }`}>
                    <AlertTriangle size={18} className={`mb-3 ${isCyberpunk ? 'text-cyber-pink' : 'text-amber-500'}`} />
                    <p className={`font-body text-sm leading-relaxed ${
                      isCyberpunk ? 'text-cyber-dim/80' : 'text-gray-600'
                    }`}>{project.challenges}</p>
                  </div>
                </DetailSection>
              )}

            </div>
          </div>

          {/* Figures - full width below the two-column grid */}
          {project.figures && project.figures.length > 0 && (() => {
            const groups = {};
            project.figures.forEach((fig, i) => {
              if (fig.group) {
                if (!groups[fig.group]) groups[fig.group] = [];
                groups[fig.group].push({ ...fig, originalIndex: i });
              }
            });
            // Track figure numbering
            let figNum = 0;
            const getFigNum = () => ++figNum;
            // Track which groups have been rendered
            const renderedGroups = new Set();

            return (
              <DetailSection
                title="Figures"
                cyberTitle="FIGURES"
                isCyberpunk={isCyberpunk}
                delay={0.3}
              >
                <div className="space-y-6">
                  {project.figures.map((fig, i) => {
                    // If it belongs to a group, render the whole group once
                    if (fig.group) {
                      if (renderedGroups.has(fig.group)) return null;
                      renderedGroups.add(fig.group);
                      const groupFigs = groups[fig.group];
                      const topLeft = groupFigs.find(f => f.groupPosition === 'top-left');
                      const topRight = groupFigs.find(f => f.groupPosition === 'top-right');
                      const bottom = groupFigs.find(f => f.groupPosition === 'bottom');
                      const tlNum = getFigNum();
                      const trNum = getFigNum();
                      const bNum = getFigNum();

                      return (
                        <motion.div
                          key={`group-${fig.group}`}
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: 0.1 }}
                          className="flex flex-col gap-3 max-w-2xl mx-auto"
                        >
                          {/* Top row - two plots side by side */}
                          <div className="grid grid-cols-2 gap-3">
                            {[{ fig: topLeft, num: tlNum }, { fig: topRight, num: trNum }].map(({ fig: f, num }) => f && (
                              <div
                                key={num}
                                className={`overflow-hidden transition-all duration-500 ${
                                  isCyberpunk
                                    ? 'border border-cyber-magenta/20 hover:border-cyber-magenta/40'
                                    : 'border border-gray-200 rounded-xl hover:shadow-lg'
                                }`}
                              >
                                <img
                                  src={f.src}
                                  alt={f.caption || `Figure ${num}`}
                                  className={`w-full h-auto ${
                                    isCyberpunk ? 'bg-cyber-dark/80' : 'bg-gray-50'
                                  }`}
                                  loading="lazy"
                                />
                                {f.caption && (
                                  <div className={`px-3 py-2 ${
                                    isCyberpunk ? 'bg-cyber-dark/50 border-t border-cyber-magenta/10' : 'bg-gray-50'
                                  }`}>
                                    <p className={`font-body text-xs leading-relaxed ${
                                      isCyberpunk ? 'text-cyber-dim/70' : 'text-gray-500'
                                    }`}>
                                      <span className={`font-mono font-semibold mr-1 ${
                                        isCyberpunk ? 'text-cyber-magenta/60' : 'text-indigo-500'
                                      }`}>Fig. {num}</span>
                                      {f.caption}
                                    </p>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                          {/* Bottom row - full width reference plot */}
                          {bottom && (
                            <div className={`overflow-hidden transition-all duration-500 ${
                              isCyberpunk
                                ? 'border border-cyber-magenta/20 hover:border-cyber-magenta/40'
                                : 'border border-gray-200 rounded-xl hover:shadow-lg'
                            }`}>
                              <img
                                src={bottom.src}
                                alt={bottom.caption || `Figure ${bNum}`}
                                className={`w-full h-auto ${
                                  isCyberpunk ? 'bg-cyber-dark/80' : 'bg-gray-50'
                                }`}
                                loading="lazy"
                              />
                              {bottom.caption && (
                                <div className={`px-3 py-2 ${
                                  isCyberpunk ? 'bg-cyber-dark/50 border-t border-cyber-magenta/10' : 'bg-gray-50'
                                }`}>
                                  <p className={`font-body text-xs leading-relaxed ${
                                    isCyberpunk ? 'text-cyber-dim/70' : 'text-gray-500'
                                  }`}>
                                    <span className={`font-mono font-semibold mr-1 ${
                                      isCyberpunk ? 'text-cyber-magenta/60' : 'text-indigo-500'
                                    }`}>Fig. {bNum}</span>
                                    {bottom.caption}
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </motion.div>
                      );
                    }

                    // Ungrouped figure
                    const num = getFigNum();
                    return (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.1 }}
                        className={`overflow-hidden transition-all duration-500 ${
                          fig.size === 'medium' ? 'max-w-xl mx-auto' : ''
                        } ${
                          isCyberpunk
                            ? 'border border-cyber-magenta/20 hover:border-cyber-magenta/40'
                            : 'border border-gray-200 rounded-xl hover:shadow-lg'
                        }`}
                      >
                        {fig.type === 'video' && fig.src ? (
                          <video
                            src={fig.src}
                            controls
                            autoPlay
                            loop
                            muted
                            playsInline
                            className={`w-full h-auto ${
                              isCyberpunk ? 'bg-cyber-dark/80' : 'bg-gray-50'
                            }`}
                          />
                        ) : fig.src ? (
                          <img
                            src={fig.src}
                            alt={fig.caption || `Figure ${num}`}
                            className={`w-full h-auto ${
                              isCyberpunk ? 'bg-cyber-dark/80' : 'bg-gray-50'
                            }`}
                            loading="lazy"
                          />
                        ) : (
                          <div className={`aspect-video flex items-center justify-center ${
                            isCyberpunk ? 'bg-cyber-dark/80' : 'bg-gray-100'
                          }`}>
                            <p className={`font-mono text-sm ${
                              isCyberpunk ? 'text-cyber-dim/40' : 'text-gray-400'
                            }`}>{fig.caption || `Figure ${num}`}</p>
                          </div>
                        )}
                        {fig.caption && (
                          <div className={`px-4 py-3 ${
                            isCyberpunk ? 'bg-cyber-dark/50 border-t border-cyber-magenta/10' : 'bg-gray-50'
                          }`}>
                            <p className={`font-body text-sm leading-relaxed ${
                              isCyberpunk ? 'text-cyber-dim/70' : 'text-gray-500'
                            }`}>
                              <span className={`font-mono font-semibold mr-2 ${
                                isCyberpunk ? 'text-cyber-magenta/60' : 'text-indigo-500'
                              }`}>Fig. {num}</span>
                              {fig.caption}
                            </p>
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              </DetailSection>
            );
          })()}

          {/* Supplemental Materials */}
          {project.supplemental && project.supplemental.length > 0 && (
            <DetailSection
              title="Supplemental Materials"
              cyberTitle="SUPPLEMENTAL"
              isCyberpunk={isCyberpunk}
              delay={0.35}
            >
              <div className="grid sm:grid-cols-2 gap-4">
                {project.supplemental.map((item, i) => {
                  const IconComp = item.type === 'pdf' ? FileText : Image;
                  return (
                    <motion.a
                      key={i}
                      href={item.src}
                      target="_blank"
                      rel="noopener noreferrer"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 }}
                      className={`group flex items-start gap-4 p-5 transition-all duration-500 ${
                        isCyberpunk
                          ? 'border border-cyber-magenta/20 bg-cyber-dark/50 hover:border-cyber-magenta/50 hover:bg-cyber-dark/80'
                          : 'border border-gray-200 bg-white rounded-xl shadow-sm hover:shadow-lg hover:border-indigo-300'
                      }`}
                    >
                      <div className={`flex-shrink-0 w-10 h-10 flex items-center justify-center transition-all duration-500 ${
                        isCyberpunk
                          ? 'border border-cyber-magenta/30 bg-cyber-dark group-hover:border-cyber-magenta/60'
                          : 'border border-gray-200 bg-gray-50 rounded-lg group-hover:border-indigo-300'
                      }`}>
                        <IconComp size={18} className={`transition-colors duration-500 ${
                          isCyberpunk ? 'text-cyber-magenta/70 group-hover:text-cyber-magenta' : 'text-gray-400 group-hover:text-indigo-500'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-display text-sm font-semibold mb-1 transition-colors duration-500 ${
                          isCyberpunk ? 'text-white group-hover:text-cyber-magenta' : 'text-gray-900 group-hover:text-indigo-600'
                        }`}>{item.label}</p>
                        <p className={`font-body text-xs leading-relaxed ${
                          isCyberpunk ? 'text-cyber-dim/60' : 'text-gray-400'
                        }`}>{item.description}</p>
                      </div>
                      <Download size={14} className={`flex-shrink-0 mt-1 transition-colors duration-500 ${
                        isCyberpunk ? 'text-cyber-dim/30 group-hover:text-cyber-magenta' : 'text-gray-300 group-hover:text-indigo-500'
                      }`} />
                    </motion.a>
                  );
                })}
              </div>
            </DetailSection>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
